import { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, X, Paperclip, CircleNotch, File as FileIcon } from "@phosphor-icons/react";

const isImg = (m) => (m || "").startsWith("image/");
const attHref = (id) => `${API_BASE}/support/attachments/${id}`;

async function uploadFiles(fileList) {
    const out = [];
    for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/support/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(data);
    }
    return out;
}

function AttachmentList({ items }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="mt-2 flex flex-wrap gap-2">
            {items.map((a) => (
                isImg(a.mime) ? (
                    <a key={a.id} href={attHref(a.id)} target="_blank" rel="noreferrer">
                        <img src={attHref(a.id)} alt={a.filename} className="h-20 w-20 rounded-lg border border-stone-200 object-cover" />
                    </a>
                ) : (
                    <a key={a.id} href={attHref(a.id)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs font-semibold text-ink hover:bg-stone-50">
                        <FileIcon size={16} className="text-brand" />
                        <span className="max-w-[160px] truncate">{a.filename}</span>
                    </a>
                )
            ))}
        </div>
    );
}

const STATUS_STYLE = {
    open: "bg-blue-100 text-blue-700",
    in_progress: "bg-amber-100 text-amber-700",
    resolved: "bg-green-100 text-green-700",
    closed: "bg-stone-200 text-stone-600",
};
const STATUSES = ["open", "in_progress", "resolved", "closed"];
const fmt = (s) => (s ? new Date(s).toLocaleString() : "");

export default function PlatformSupportPanel() {
    const [tickets, setTickets] = useState([]);
    const [counts, setCounts] = useState({});
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/platform/support-tickets", { params: filter ? { status: filter } : {} });
            setTickets(data.tickets || []);
            setCounts(data.counts || {});
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="platform-support-panel">
            <div className="mb-4 flex flex-wrap gap-2">
                <button onClick={() => setFilter("")} data-testid="support-filter-all"
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold ${filter === "" ? "bg-ink text-white" : "border border-stone-300 text-stone-600 hover:bg-stone-100"}`}>
                    All
                </button>
                {STATUSES.map((s) => (
                    <button key={s} onClick={() => setFilter(s)} data-testid={`support-filter-${s}`}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${filter === s ? "bg-ink text-white" : "border border-stone-300 text-stone-600 hover:bg-stone-100"}`}>
                        {s.replace("_", " ")} <span className="ml-1 opacity-70">{counts[s] ?? 0}</span>
                    </button>
                ))}
            </div>

            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
                <table className="w-full text-left text-sm">
                    <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone2">
                        <tr>
                            <th className="px-5 py-3">Subject</th>
                            <th className="px-5 py-3">Workspace</th>
                            <th className="px-5 py-3">Priority</th>
                            <th className="px-5 py-3">Status</th>
                            <th className="px-5 py-3">Updated</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="px-5 py-12 text-center text-stone2">Loading…</td></tr>
                        ) : tickets.length === 0 ? (
                            <tr><td colSpan={5} className="px-5 py-12 text-center text-stone2" data-testid="platform-support-empty">No tickets.</td></tr>
                        ) : tickets.map((t) => (
                            <tr key={t.id} data-testid={`platform-ticket-${t.id}`} onClick={() => setActive(t)}
                                className="cursor-pointer border-t border-stone-100 hover:bg-stone-50">
                                <td className="px-5 py-4 font-semibold">{t.subject}</td>
                                <td className="px-5 py-4 text-stone-600">{t.org_name || "—"}</td>
                                <td className="px-5 py-4 capitalize">{t.priority}</td>
                                <td className="px-5 py-4">
                                    <span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[t.status] || ""}`}>{(t.status || "").replace("_", " ")}</span>
                                </td>
                                <td className="px-5 py-4 text-stone-500">{fmt(t.updated_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {active && <PlatformTicketDrawer ticket={active} onClose={() => setActive(null)} onChanged={load} />}
        </div>
    );
}

function PlatformTicketDrawer({ ticket, onClose, onChanged }) {
    const [t, setT] = useState(ticket);
    const [body, setBody] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/platform/support-tickets", { params: {} });
            const found = (data.tickets || []).find((x) => x.id === ticket.id);
            if (found) setT(found);
        } catch { /* ignore */ }
    }, [ticket.id]);

    const onPick = async (e) => {
        const files = e.target.files;
        if (!files?.length) return;
        setUploading(true);
        try {
            const up = await uploadFiles(files);
            setAttachments((x) => [...x, ...up]);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const send = async () => {
        if (!body.trim() && attachments.length === 0) return;
        setBusy(true);
        try {
            await api.post(`/platform/support-tickets/${t.id}/reply`, { body, attachments });
            setBody("");
            setAttachments([]);
            await refresh();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const setStatus = async (status) => {
        try {
            await api.put(`/platform/support-tickets/${t.id}/status`, { status });
            setT((x) => ({ ...x, status }));
            onChanged?.();
            toast.success(`Marked ${status.replace("_", " ")}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" data-testid="platform-ticket-drawer" onClick={onClose}>
            <div className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between border-b border-stone-200 p-5">
                    <div className="min-w-0">
                        <div className="truncate font-heading text-lg font-bold">{t.subject}</div>
                        <div className="mt-1 text-xs text-stone2">{t.org_name} · {t.created_by_email}</div>
                    </div>
                    <button onClick={onClose} className="text-stone2 hover:text-ink" data-testid="platform-ticket-close"><X size={20} /></button>
                </div>

                <div className="flex flex-wrap gap-1.5 border-b border-stone-100 px-5 py-3">
                    {STATUSES.map((s) => (
                        <button key={s} data-testid={`set-status-${s}`} onClick={() => setStatus(s)}
                            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${t.status === s ? "bg-ink text-white" : "border border-stone-300 text-stone-600 hover:bg-stone-100"}`}>
                            {s.replace("_", " ")}
                        </button>
                    ))}
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto p-5">
                    {(t.messages || []).map((m) => {
                        const support = m.author_role === "support";
                        return (
                            <div key={m.id} className={`flex ${support ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${support ? "bg-indigo-100 text-ink" : "bg-stone-100 text-ink"}`}>
                                    <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-stone2">
                                        {support ? "You (Support)" : (m.author_name || "Customer")}
                                    </div>
                                    <div className="whitespace-pre-wrap">{m.body}</div>
                                    <AttachmentList items={m.attachments} />
                                    <div className="mt-1 text-[10px] text-stone2">{fmt(m.created_at)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-end gap-2 border-t border-stone-200 p-4">
                    <input ref={fileRef} type="file" multiple hidden onChange={onPick} data-testid="platform-reply-file-input" />
                    <button type="button" data-testid="platform-reply-attach" onClick={() => fileRef.current?.click()} disabled={uploading}
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-stone-300 text-stone-600 hover:bg-stone-100 disabled:opacity-50">
                        {uploading ? <CircleNotch size={17} className="animate-spin" /> : <Paperclip size={17} />}
                    </button>
                    <textarea data-testid="platform-reply-input" value={body} onChange={(e) => setBody(e.target.value)} rows={2}
                        className="flex-1 resize-none rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" placeholder="Reply to the customer…" />
                    <button data-testid="platform-reply-send" onClick={send} disabled={busy || uploading || (!body.trim() && attachments.length === 0)}
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-white hover:opacity-90 disabled:opacity-40">
                        <PaperPlaneRight size={18} weight="fill" />
                    </button>
                </div>
            </div>
        </div>
    );
}
